import { PROMISE_SUPERSEDED_ERROR_MESSAGE } from '@noths/polaris-client-utils';
import { createSlice } from '@reduxjs/toolkit';

import { requestProducts } from './thunks/requestProducts';
import type { ProductsState, RequestProductsActionProductsPayload } from './types';
import { TriggerName } from './types';
import { getNextPageNumberNotLoaded, getPreviousPageNumberNotLoaded } from './utils/pageNumbers';

export const initialState: Readonly<ProductsState> = {
  currencyCode: 'GBP',
  lastPageLoaded: null,
  pageLoading: null,
  pages: {},
  productsReturnedCount: 0,
  providerAttributionToken: null,
  providerMetadata: null,
  totalPages: 0,
  totalProducts: 0,
};

const populateProducts = (state: ProductsState, data: RequestProductsActionProductsPayload) => {
  const {
    currencyCode,
    lastPageLoaded,
    products,
    productsReturnedCount,
    providerAttributionToken,
    providerMetadata,
    totalPages,
    totalProducts,
  } = data;

  return {
    ...state,
    currencyCode,
    error: null,
    lastPageLoaded,
    pages: {
      ...state.pages,
      ...{ [lastPageLoaded]: products },
    },
    pageLoading: null,
    productsReturnedCount,
    providerAttributionToken,
    providerMetadata,
    totalPages,
    totalProducts,
  } as ProductsState;
};

export const { actions, reducer } = createSlice({
  name: 'products',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(requestProducts.pending, (state, { meta }) => {
        switch (meta.arg.triggerName) {
          case TriggerName.LOAD_MORE_PRODUCTS: {
            state.pageLoading = getNextPageNumberNotLoaded(state.pages, state.totalPages);
            break;
          }
          case TriggerName.LOAD_PREVIOUS_PRODUCTS: {
            state.pageLoading = getPreviousPageNumberNotLoaded(state.pages);
            break;
          }
          case TriggerName.CHANGE_FACETS:
          case TriggerName.CHANGE_SORT:
            state.pages = {};
            state.pageLoading = 1;
            break;
        }
      })
      .addCase(requestProducts.fulfilled, (state, { payload }) => {
        if (payload.products) {
          state = populateProducts(state, payload.products);
        }

        return state;
      })
      .addCase(requestProducts.rejected, (state, action) => {
        if (action.error && action.error.message === PROMISE_SUPERSEDED_ERROR_MESSAGE) {
          return;
        }

        state.pages = {};
        state.error = action.error;
        state.pageLoading = null;
      });
  },
});
